import axios from "axios";
import { entityResultsScreen, throwError, toggleLoading } from "./navigationWorker";
import { getXplanURL, APICalls, getPayload, handleResponseData, SetPermissionList } from "./helper";
import { LogError } from "../actions/errorWorker";

// Cache TTL constants (in milliseconds)
const CACHE_TTL = {
	UFIELD: 15 * 60 * 1000,      // 15 minutes for user fields
	CAPABILITIES: 30 * 60 * 1000, // 30 minutes for capabilities
};

// Helper to check if cache is still valid
function isCacheValid(key: string, ttl: number): boolean {
	const cached = localStorage.getItem(key);
	if (!cached) return false;
	
	const timestamp = localStorage.getItem(`${key}_timestamp`);
	if (!timestamp) return true; // Legacy cache without timestamp
	
	return Date.now() - parseInt(timestamp) < ttl;
}

// Helper to set cache with timestamp
function setCache(key: string, value: any): void {
	localStorage.setItem(key, JSON.stringify(value));
	localStorage.setItem(`${key}_timestamp`, Date.now().toString());
}

// Memoization cache for makeSearchParams
const searchParamsCache = new Map<string, string>();

export async function entity_quick_search(searchCriteria, entityType, entityStatus): Promise<any[]> {
	const URLParam = makeSearchParams(entityType, searchCriteria, entityStatus);
	const payload = getPayload(`${getXplanURL()}${APICalls.EntitySearch}${URLParam}`, null, "GET");
	
	try {
		const response = await axios.post(APICalls.AWSUrl(), payload);
		
		// Early return on error
		if (response.data?.isError === true) {
			return [];
		}
		
		// Parse data efficiently - only parse if it's a string
		const rawData = response.data?.data;
		if (!rawData) return [];
		
		const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
		
		// Return empty array if no results
		if (!data || (Array.isArray(data) && data.length === 0)) {
			return [];
		}
		
		return Array.isArray(data) ? data : [];
	} catch (error) {
		console.error("Entity quick search error:", error);
		LogError(error, { payload }, "entity_quick_search", "ERROR");
		return [];
	}
}

export async function client_search(searchCriteria, entityType, entityStatus) {
	const URLParam = makeSearchParams(entityType, searchCriteria, entityStatus);
	const payload = getPayload(`${getXplanURL()}${APICalls.EntitySearch}${URLParam}`, null, "GET");
	
	try {
		const response = await axios.post(APICalls.AWSUrl(), payload);
		
		if (response.data.isError === true) {
			toggleLoading(false);
			return;
		}
		
		const data = handleResponseData(
			response,
			"No data found matching the search criteria."
		);
		
		if (data) {
			entityResultsScreen(false, data);
			sessionStorage.setItem("search_results", JSON.stringify(data));
		} else {
			entityResultsScreen(true, []);
			sessionStorage.removeItem("search_results");
			throw new Error("No data found matching the search criteria.");
		}
	} catch (error) {
		throwError(error);
		LogError(error, { payload }, "client_search", "ERROR");
	}
}

export async function get_choice_field_dependence(
	group: string,
	field_name: string,
	callback: any,
	callback_data: any = null,
	loadUFields: boolean = false
) {
	const key = `${group}_${field_name}_depend_results`;
	
	// Return early if cached, valid, and not forcing reload
	if (isCacheValid(key, CACHE_TTL.UFIELD) && !loadUFields) {
		if (callback) callback(callback_data, false);
		return;
	}
	
	const payload = getPayload(
		`${getXplanURL()}${APICalls.Ufieldsearch}${group}/${field_name}/choice_category_dependence`
	);
	
	try {
		const response = await axios.post(APICalls.AWSUrl(), payload);
		const data = handleResponseData(response, "No ufield values found.");
		
		if (data) {
			setCache(key, data.isError ? {} : data);
			if (callback) callback(callback_data, false);
		}
	} catch (error) {
		throwError(error);
		LogError(error, { payload }, "get_choice_field_dependence", "ERROR");
	}
}

export async function get_ufield_values(group: string, field: string, loadUfields: boolean) {
	const key = `${group}_${field}_results`;

	// Return early if cached, valid, and not forcing reload
	if (isCacheValid(key, CACHE_TTL.UFIELD) && !loadUfields) return;

	const payload = getPayload(`${getXplanURL()}${APICalls.Ufieldsearch}${group}/${field}/choice`);

	try {
		const response = await axios.post(APICalls.AWSUrl(), payload);
		const data = handleResponseData(response, "No ufield values found.");
		if (data) setCache(key, data);
	} catch (error) {
		throwError(error);
		LogError(error, { payload }, "get_ufield_values", "ERROR");
	}
}

export async function get_capabilities(successCallback, caps) {
	// Return early if already cached and valid
	if (isCacheValid("capabilities_results", CACHE_TTL.CAPABILITIES)) {
		if (successCallback) successCallback(caps);
		SetPermissionList();
		return;
	}

	const payload = getPayload(`${getXplanURL()}${APICalls.Capabilities}`, null, "GET");
	
	try {
		const response = await axios.post(APICalls.AWSUrl(), payload);
		const data = handleResponseData(response, "No capabilities values found.");
		
		if (data) {
			setCache("capabilities_results", data);
			if (successCallback) successCallback(caps);
			SetPermissionList();
		}
	} catch (error) {
		throwError(error);
		LogError(error, { payload }, "get_capabilities", "ERROR");
	}
}

// Cache parsed capabilities to avoid repeated JSON.parse
let cachedCapabilities: string[] | null = null;
let cachedCapabilitiesKey: string | null = null;

export function has_capability(capability: string): boolean {
	const currentKey = localStorage.getItem("capabilities_results");
	
	// Re-parse only if cache key changed
	if (currentKey !== cachedCapabilitiesKey) {
		try {
			cachedCapabilities = currentKey ? JSON.parse(currentKey) : [];
			cachedCapabilitiesKey = currentKey;
		} catch {
			cachedCapabilities = [];
			cachedCapabilitiesKey = null;
		}
	}
	
	return cachedCapabilities?.includes(capability) ?? false;
}

export function makeSearchParams(entityType, fields, statuses = []) {
	// Create cache key from inputs
	const cacheKey = JSON.stringify({ entityType, fields, statuses });
	
	// Return cached result if available
	if (searchParamsCache.has(cacheKey)) {
		return searchParamsCache.get(cacheKey)!;
	}
	
	let criteria = "";
	if (!fields) {
		return entityType;
	}
	
	switch (entityType) {
		case "client-v2":
			criteria = `?quicksearch=${encodeURIComponent(fields["entityName"] || "")}`;
			
			if (fields["pcode"]) {
				criteria += ` pcode:${encodeURIComponent(fields["pcode"])}`;
			}

			if (fields["dob"]) {
				criteria += ` dob:${encodeURIComponent(fields["dob"])}`;
			}

			["dob", "preferred_postcode", "client_adviser_name"].forEach((x, index) => {
				criteria += `&fields.${index}=${encodeURIComponent(x)}`;
			});

			statuses.forEach((x, index) => {
				criteria += `&include_client_status.${index}=${encodeURIComponent(x)}`;
			});
			break;
			
		case "user":
		case "referrer":
		case "supplier":
		case "profadviser":
			criteria = `?quicksearch=${encodeURIComponent(fields["entityName"] || "")}`;
			break;
	}

	const result = entityType + criteria;
	
	// Cache the result (limit cache size to 100 entries)
	if (searchParamsCache.size > 100) {
		const firstKey = searchParamsCache.keys().next().value;
		searchParamsCache.delete(firstKey);
	}
	searchParamsCache.set(cacheKey, result);
	
	return result;
}
